import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import {
  Environment,
  useTexture,
  DeviceOrientationControls,
  PointerLockControls,
  PerspectiveCamera,
} from '@react-three/drei';
import { useXR, Controllers, Hands, useTeleportation, Interactive } from '@react-three/xr';
import { OrbitControls } from '@react-three/drei';
import { useSnapshot } from 'valtio';
import { useControls } from 'leva';

const SceneContent = lazy(() => import('./SceneContent'));

import { ASSET_PATH } from './config';
import { Loader } from './Loader';
import { state } from './state';
// import XRStats from './XRStats';

export function XRScene({ data }) {
  const { isPresenting } = useXR();
  const { requestedVR, runFallback, useDeviceOrientation } = useSnapshot(state);
  const { yCenter, zCenter } = useControls({
    yCenter: { value: 1.3, min: 0, max: 2 },
    zCenter: { value: -1, min: -1.5, max: -0.2 },
  });

  useEffect(
    function disablePageScroll() {
      if (runFallback) {
        document.documentElement.style.overflow = 'hidden';
      }
      return () => (document.documentElement.style.overflow = '');
    },
    [runFallback],
  );

  const groupRef = useRef();

  return (
    <Suspense fallback={null}>
      <>
        {(isPresenting || runFallback) && (
          <Suspense fallback={<Loader />}>
            <Controllers renderOrder={100} />
            <Hands />
            <group ref={groupRef}>
              {/* Move scene content back a bit at initial spawn of scene */}
              <SceneContent yCenter={yCenter} zCenter={zCenter} data={data} />
            </group>
          </Suspense>
        )}

        {/* the Hdri is not a fan of lazy loading */}
        <Environment files={ASSET_PATH + 'hdri/drakensberg_solitary_mountain_puresky_256.hdr'} />
        {/* For some weird reason this needs to be here for the
            HDR lighting to work or the scene turns blue-ish */}
        <mesh scale={0}>
          <meshStandardMaterial />
        </mesh>
      </>
      {/* Render background in VR or runFallback */}
      {((isPresenting && requestedVR) || (!isPresenting && runFallback)) && <VrBackground />}

      {runFallback && <PerspectiveCamera makeDefault position={[0, yCenter + 0.2, 0]} fov={70} />}
      {runFallback && !useDeviceOrientation && (
        <OrbitControls
          target={[0, yCenter, zCenter]}
          onStart={() => {
            state.hasInteracted = true;
          }}
        />
      )}
      {runFallback && useDeviceOrientation && <DeviceOrientationControls />}
      {/* {runFallback && <XRStats />} */}
    </Suspense>
  );
}

function VrBackground() {
  const vrBg = useTexture(ASSET_PATH + 'panoramas/agog-gradient-env-4096.min.jpg');
  return <Environment map={vrBg} background="only" />;
}
